/**
 * Score formatting utilities — SINGLE SOURCE OF TRUTH for all score display.
 * Internal scores are 0-100. Display scores are 0-10.
 * No other score math should exist in components.
 */

/** Convert a 0-100 internal score to a "X.X" string on 0-10 scale. */
export function formatScore100to10(score: number): string {
  const normalized = Math.max(0, Math.min(10, score / 10));
  return normalized.toFixed(1);
}

/** Convert a 0-100 internal score to a formatted "X.X / 10" label. */
export function formatScoreWithLabel(score: number): string {
  return `${formatScore100to10(score)} / 10`;
}

/** Convert a 0-100 internal score to its numeric 0-10 value. */
export function normalizeScoreTo10(score: number): number {
  return Math.max(0, Math.min(10, Math.round(score) / 10));
}

/**
 * Convert a RISK score into a 0-10 SAFETY score.
 * Higher risk → lower safety.
 *
 * @param riskScore  The risk value to invert.
 * @param scale      Maximum of the input range (default 10 for API overall scores).
 *                   Pass 100 for domain-level scores (walletIntel, contractSafety, etc.).
 *
 * Examples:
 *   formatSafetyScore(9.5, 10)  → 0.5   (high-risk token)
 *   formatSafetyScore(0.2, 10)  → 9.8   (safe token)
 *   formatSafetyScore(70, 100)  → 3.0   (domain-level risk)
 */
export function formatSafetyScore(riskScore?: number | null, scale: 10 | 100 = 10): number | null {
  if (riskScore === undefined || riskScore === null) return null;
  const clamped = Math.max(0, Math.min(scale, riskScore));
  const safetyScore = ((scale - clamped) / scale) * 10;
  return Number(safetyScore.toFixed(1));
}
